import { useEffect, useRef, type ReactNode } from "react";
import { colors } from "../design-system";
import { AppPressable } from "./AppPressable";
import { AppText } from "./AppText";
import { Button } from "./Button";
import { Icon, type IconName } from "./Icon";

export type DialogTone = "success" | "danger" | "warning" | "info";

type DialogAction = {
  label: string;
  onPress: () => void;
  /** Action destructrice (supprimer, annuler une course…). */
  danger?: boolean;
  loading?: boolean;
};

type AppDialogProps = {
  visible: boolean;
  onClose: () => void;
  tone?: DialogTone;
  title: string;
  message?: string;
  /** Lignes de détail (« Order Details » de la référence) : libellé → valeur. */
  details?: { label: string; value: string }[];
  /** Action principale, mise en avant. */
  confirm?: DialogAction;
  /** Action secondaire, en contour. */
  cancel?: DialogAction;
  /** Contenu libre inséré avant les actions. */
  children?: ReactNode;
  /** Empêche la fermeture au clic sur le fond (décision obligatoire). */
  dismissOnBackdrop?: boolean;
};

// Un ton = une icône + un duo (fond doux, trait soutenu). Les couleurs viennent
// TOUTES du design system — aucune valeur inventée.
const TONE: Record<DialogTone, { icon: IconName; ring: string; fg: string }> = {
  success: { icon: "checkmark", ring: colors.success100, fg: colors.success600 },
  danger: { icon: "alert", ring: colors.sos200, fg: colors.sos600 },
  warning: { icon: "warning-outline", ring: colors.warn50, fg: colors.warn600 },
  info: { icon: "information", ring: colors.navy50, fg: colors.navy600 },
};

/**
 * Dialogue centré — remplace l'alerte système.
 *
 * ## Pourquoi pas l'alerte native
 * `Alert.alert` n'était pas stylable : police, couleurs et disposition imposées
 * par l'OS. Le moment le plus critique du parcours (« votre retrait a échoué »,
 * « annuler la course ? ») cassait donc l'identité de l'app. L'argument vaut
 * identiquement pour `window.confirm` sur web, qui est en plus BLOQUANT pour le
 * fil JS — raison de plus de ne jamais l'utiliser ici.
 *
 * ## Anatomie
 *   ┌──────────────────────┐
 *   │        ( ✓ )         │  pastille de statut, cerclée
 *   │        Titre         │  centré, fort
 *   │       Message        │
 *   │   libellé   valeur   │  lignes de détail
 *   │  [   Annuler     ]   │  secondaire d'abord
 *   │  [   Confirmer   ]   │
 *   └──────────────────────┘
 *
 * ## Apports du `<dialog>` natif
 * Focus piégé, fermeture par Échap, fond inertisé pour les lecteurs d'écran :
 * autant de comportements que la version React Native n'avait pas et qu'il
 * aurait fallu implémenter à la main. `aria-modal` et le rôle sont implicites.
 *
 * L'ordre des boutons (secondaire AVANT primaire) est conservé : on ne valide
 * pas par réflexe. Il devient d'autant plus important sur web, où la touche
 * Entrée peut activer le bouton focalisé.
 */
export function AppDialog({
  visible,
  onClose,
  tone = "info",
  title,
  message,
  details,
  confirm,
  cancel,
  children,
  dismissOnBackdrop = true,
}: AppDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const t = TONE[tone];

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (visible && !el.open) el.showModal();
    if (!visible && el.open) el.close();
  }, [visible]);

  if (!visible) return null;

  return (
    <dialog
      ref={ref}
      aria-label={title}
      onCancel={(e) => {
        if (!dismissOnBackdrop) {
          e.preventDefault();
          return;
        }
        onClose();
      }}
      onClick={dismissOnBackdrop ? onClose : undefined}
      className="fixed inset-0 m-0 flex h-full max-h-full w-full max-w-full items-center justify-center border-none bg-transparent p-0 px-6 backdrop:bg-navy-900/55"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm animate-[alomain-modal-in_220ms_ease-out] rounded-card bg-white px-6 pb-5 pt-7"
      >
        {/* Pastille de statut : cercle plein + anneau plus clair autour. */}
        <div className="flex flex-col items-center">
          <span
            className="flex h-16 w-16 items-center justify-center rounded-full"
            style={{ backgroundColor: t.ring }}
          >
            <Icon name={t.icon} size={30} color={t.fg} />
          </span>

          <AppText as="h2" variant="h3" color={colors.navy800} className="mt-4 block text-center">
            {title}
          </AppText>

          {message ? (
            <AppText as="p" variant="small" color={colors.gray500} className="mt-2 block text-center">
              {message}
            </AppText>
          ) : null}
        </div>

        {details && details.length > 0 && (
          // <dl> plutôt qu'une pile de <div> : c'est littéralement une liste de
          // paires libellé/valeur, et les lecteurs d'écran les associent.
          <dl className="mt-5">
            {details.map((d) => (
              <div key={d.label} className="flex flex-row items-center justify-between py-2">
                <AppText as="dt" variant="small" color={colors.gray500}>
                  {d.label}
                </AppText>
                <AppText as="dd" variant="smallBold" color={colors.navy800} className="truncate">
                  {d.value}
                </AppText>
              </div>
            ))}
          </dl>
        )}

        {children}

        {(cancel || confirm) && (
          <div className="mt-5 flex flex-col gap-2.5">
            {/* Secondaire d'abord : on ne valide pas par réflexe. */}
            {cancel && (
              <AppPressable
                onClick={cancel.onPress}
                className="flex h-14 items-center justify-center rounded-btn border border-gray-200"
              >
                <AppText variant="button" color={colors.navy800}>
                  {cancel.label}
                </AppText>
              </AppPressable>
            )}

            {confirm && (
              <Button
                title={confirm.label}
                onClick={confirm.onPress}
                variant={confirm.danger ? "sos" : "primary"}
                loading={confirm.loading}
              />
            )}
          </div>
        )}
      </div>
    </dialog>
  );
}
